import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { api } from "../api";
import { extractApiError } from "../lib/errors";
import { useNavigate } from "react-router-dom";
import { Bicycle, Calendar, CalendarBlank, Check, Clock, ArrowSquareOut, Heart, Plus, TrendUp, MapPin, Mountains, Compass, MagnifyingGlass, Sparkle, Trophy, Waves, X, Icon as IconCmp } from "@phosphor-icons/react";
import { FABSpeedDial } from "../components/fab";
import {
  EventCard,
  RaceDiscipline,
  nameForLang,
  toneClasses,
  useDismissEvent,
  useEventRecommendations,
  useSaveEvent,
  useSnoozeEvent,
  useUnsaveEvent,
} from "../eventsApi";
import { SneakerIcon } from "../components/SneakerIcon";
import { SuggestMissingEventCTA } from "../components/SuggestMissingEventCTA";
import { disciplineToSportProfile } from "../lib/disciplineToSportProfile";
import { EmptyState } from "../components/EmptyState";
import { useUpdateCoords } from "../profileApi";
import { Card } from "../components/ui/Card";
import { Input } from "../components/ui/Input";


function GeolocationPrompt() {
  const { t } = useTranslation();
  const updateCoords = useUpdateCoords();
  const [error, setError] = useState<string | null>(null);
  const [pending, setPending] = useState(false);

  const onAsk = () => {
    setError(null);
    if (!navigator.geolocation) {
      setError(t("events_geo_error_unsupported"));
      return;
    }
    setPending(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        updateCoords.mutate(
          { lat: pos.coords.latitude, lon: pos.coords.longitude },
          {
            onSettled: () => setPending(false),
            onError: (e: unknown) => {
              setError(extractApiError(e, t("events_geo_save_error")));
              setPending(false);
            },
          },
        );
      },
      (err) => {
        setPending(false);
        if (err.code === 1) setError(t("events_geo_error_denied"));
        else setError(t("events_geo_error_failed"));
      },
      { timeout: 10000, maximumAge: 600000 },
    );
  };

  return (
    <div className="p-3 rounded-pill bg-bg-raised/60 ring-1 ring-bg-divider space-y-2">
      <div className="text-[12px] text-fg-secondary leading-relaxed">
        {t("events_geo_hint")}
      </div>
      <button
        type="button"
        onClick={onAsk}
        disabled={pending}
        className="w-full px-3 py-2 rounded-pill ring-1 ring-accent/40 bg-accent/15 text-accent text-[12px] font-medium disabled:opacity-50 inline-flex items-center justify-center gap-2"
      >
        <MapPin className="w-3.5 h-3.5" />
        {pending ? t("events_geo_requesting") : t("events_geo_allow")}
      </button>
      {error && (
        <div className="text-[11px] text-bad">{error}</div>
      )}
    </div>
  );
}

const DISCIPLINE_OPTIONS: { key: RaceDiscipline | "any"; i18nKey: string }[] = [
  { key: "any",       i18nKey: "events_discipline_any" },
  { key: "run",       i18nKey: "sport.run" },
  { key: "trail",     i18nKey: "sport.trail" },
  { key: "triathlon", i18nKey: "sport.triathlon" },
  { key: "duathlon",  i18nKey: "sport.duathlon" },
  { key: "swim",      i18nKey: "sport.swim" },
  // Sprint 39f — фитнес-вертикаль (Hyrox + future Crossfit/Spartan/OCR).
  { key: "hyrox",     i18nKey: "events_discipline_hyrox" },
  { key: "ride",      i18nKey: "sport.ride" },
];

// Sprint 39g — пресеты дистанций под каждую дисциплину.
// Триатлон/duathlon: distance_km в каталоге = total (swim+bike+run).
// Окно ±25% от номинала чтобы вариации race captured.
type DistancePreset = { key: string; i18nKey: string; minKm: number; maxKm: number };
const DISTANCE_PRESETS: Record<string, DistancePreset[]> = {
  run: [
    { key: "5k",       i18nKey: "events_dist_preset_run_5k",       minKm: 4,    maxKm: 6 },
    { key: "10k",      i18nKey: "events_dist_preset_run_10k",      minKm: 8,    maxKm: 12 },
    { key: "15k",      i18nKey: "events_dist_preset_run_15k",      minKm: 13,   maxKm: 20.5 },
    { key: "half",     i18nKey: "events_dist_preset_run_half",     minKm: 20.5, maxKm: 23 },
    { key: "marathon", i18nKey: "events_dist_preset_run_marathon", minKm: 40,   maxKm: 44 },
    { key: "ultra",    i18nKey: "events_dist_preset_run_ultra",    minKm: 50,   maxKm: 500 },
  ],
  trail: [
    { key: "trail-short", i18nKey: "events_dist_preset_trail_short", minKm: 0,   maxKm: 20 },
    { key: "trail-mid",   i18nKey: "events_dist_preset_trail_mid",   minKm: 21,  maxKm: 50 },
    { key: "trail-long",  i18nKey: "events_dist_preset_trail_long",  minKm: 51,  maxKm: 100 },
    { key: "trail-utmb",  i18nKey: "events_dist_preset_trail_utmb",  minKm: 100, maxKm: 500 },
  ],
  triathlon: [
    { key: "tri-sprint",  i18nKey: "events_dist_preset_tri_sprint",  minKm: 18,  maxKm: 32 },
    { key: "tri-olympic", i18nKey: "events_dist_preset_tri_olympic", minKm: 45,  maxKm: 60 },
    { key: "tri-70.3",    i18nKey: "events_dist_preset_tri_703",     minKm: 100, maxKm: 130 },
    { key: "tri-im",      i18nKey: "events_dist_preset_tri_im",      minKm: 200, maxKm: 240 },
  ],
  duathlon: [
    { key: "du-sprint",   i18nKey: "events_dist_preset_du_sprint",   minKm: 15, maxKm: 35 },
    { key: "du-standard", i18nKey: "events_dist_preset_du_standard", minKm: 40, maxKm: 60 },
    { key: "du-long",     i18nKey: "events_dist_preset_du_long",     minKm: 60, maxKm: 100 },
  ],
  swim: [
    { key: "sw-short", i18nKey: "events_dist_preset_sw_short", minKm: 0, maxKm: 2 },
    { key: "sw-5k",    i18nKey: "events_dist_preset_sw_5k",    minKm: 4, maxKm: 6 },
    { key: "sw-10k",   i18nKey: "events_dist_preset_sw_10k",   minKm: 9, maxKm: 50 },
  ],
  hyrox: [
    { key: "hyrox-standard", i18nKey: "events_dist_preset_hyrox", minKm: 7, maxKm: 9 },
  ],
};

// Sprint 39e — слово «Major» только для WMM (официальные мейджоры).
// Локальные большие старты — «Премьер». IRONMAN-брендовые триатлоны
// получают свой лейбл через override ниже.
const TIER_I18N_KEY: Record<EventCard["tier"], string> = {
  wmm: "events_tier_wmm",
  major_local: "events_tier_major_local",
  series: "events_tier_series",
  classic: "events_tier_classic",
};

const TIER_COLOR: Record<EventCard["tier"], string> = {
  wmm: "bg-warn/15 text-warn ring-1 ring-warn/30",
  major_local: "bg-accent/15 text-accent ring-1 ring-accent/30",
  series: "bg-accent/15 text-accent ring-1 ring-accent/30",
  classic: "bg-bg-raised text-fg-muted ring-1 ring-bg-divider",
};

const DISCIPLINE_ICON: Record<RaceDiscipline, IconCmp | "sneaker"> = {
  run: "sneaker",
  trail: Mountains,
  triathlon: Compass,
  duathlon: Compass,
  swim: Waves,
  hyrox: Compass,
  ride: Bicycle,
};

function disciplineIcon(d: RaceDiscipline) {
  const Icon = DISCIPLINE_ICON[d] || Compass;
  if (Icon === "sneaker") {
    return <SneakerIcon size={16} strokeWidth={1.75} />;
  }
  return <Icon className="w-4 h-4" strokeWidth={1.75} />;
}

type TFunc = (key: string, opts?: Record<string, unknown>) => string;

function fmtDate(iso: string | null, t: TFunc, locale: string, typicalMonth: number | null = null): string {
  if (!iso) {
    if (typicalMonth && typicalMonth >= 1 && typicalMonth <= 12) {
      // Sprint 39g — год в подписи, чтобы юзер не подумал что апрель
      // (если уже май) — это «прошлый месяц».
      const now = new Date();
      const currentMonth = now.getMonth() + 1;
      const yr = typicalMonth >= currentMonth ? now.getFullYear() : now.getFullYear() + 1;
      const month = new Intl.DateTimeFormat(locale, { month: "long" }).format(new Date(yr, typicalMonth - 1, 1));
      return t("events_date_typically", { month, year: yr });
    }
    return t("events_date_tbd");
  }
  const d = new Date(iso);
  const now = new Date();
  const days = Math.round((d.getTime() - now.getTime()) / 86400000);
  const dStr = d.toLocaleDateString(locale, { day: "2-digit", month: "short", year: "numeric" });
  if (days < 0) return `${dStr} · ${t("events_date_past")}`;
  if (days === 0) return `${dStr} · ${t("events_date_today")}`;
  if (days === 1) return `${dStr} · ${t("events_date_tomorrow")}`;
  if (days < 30) return `${dStr} · ${t("events_date_days", { days })}`;
  if (days < 365) return `${dStr} · ${t("events_date_months", { months: Math.round(days / 30) })}`;
  return dStr;
}

function fmtDistance(km: number | null, kmShort: string): string {
  if (km === null) return "";
  if (km < 1) return `<1 ${kmShort}`;
  if (km < 100) return `${Math.round(km)} ${kmShort}`;
  if (km < 1000) return `${Math.round(km / 10) * 10} ${kmShort}`;
  return `${Math.round(km / 100) * 100} ${kmShort}`;
}

function ScoreFactors({ card }: { card: EventCard }) {
  const { t } = useTranslation();
  const factors = [
    { label: t("events_factor_tier"),       value: card.factor_tier,       kind: "neutral" as const },
    { label: t("events_factor_geo"),        value: card.factor_geo,        kind: "neutral" as const },
    { label: t("events_factor_discipline"), value: card.factor_discipline, kind: "neutral" as const },
    { label: t("events_factor_goal"),       value: card.factor_goal,       kind: "neutral" as const },
    { label: t("events_factor_season"),     value: card.factor_season,     kind: "tone" as const },
    { label: t("events_factor_volume"),     value: card.factor_volume,     kind: "neutral" as const },
  ];
  // W-REBUILD-6: сырые очки рейтинга («Уровень +40») — внутренняя кухня
  // подбора, юзеру показываем только ПРИЧИНЫ совпадения (топ-3 факторов),
  // без чисел.
  const top = factors
    .filter((f) => f.value > 0)
    .sort((a, b) => b.value - a.value)
    .slice(0, 3);
  if (top.length === 0) return null;
  return (
    <div className="flex gap-1.5 flex-wrap text-[10px]">
      {top.map((f) => (
        <span
          key={f.label}
          className="px-1.5 py-0.5 rounded-full bg-bg-raised text-fg-primary ring-1 ring-bg-divider"
        >
          {f.label}
        </span>
      ))}
    </div>
  );
}

type EventCardItemProps = {
  card: EventCard;
  /** Включить ряд кнопок ✕/♥/⏸ или снимать сохранение */
  showActions?: boolean;
};

export function EventCardItem({ card, showActions = true }: EventCardItemProps) {
  const { t, i18n } = useTranslation();
  const nav = useNavigate();
  const tone = toneClasses(card);
  const kmShort = t("km_short");
  // Sprint 30/39e — discipline-aware tier-label override:
  //   Hyrox      → «Hyrox» (отдельный визуал, не Major/Премьер)
  //   IRONMAN    → «IRONMAN» (триатлоны со slug im-* — брендовая серия)
  const isHyrox = card.discipline === "hyrox";
  const isIronman = card.discipline === "triathlon" && card.slug.startsWith("im-");
  const tierBadge = isHyrox
    ? "bg-warn/15 text-warn ring-1 ring-warn/40"
    : isIronman
      ? "bg-bad/15 text-bad ring-1 ring-bad/40"
      : TIER_COLOR[card.tier];
  const tierLabel = isHyrox
    ? "Hyrox"
    : isIronman
      ? "IRONMAN"
      : t(TIER_I18N_KEY[card.tier]);

  const dismissMut = useDismissEvent();
  const saveMut = useSaveEvent();
  const snoozeMut = useSnoozeEvent();
  const unsaveMut = useUnsaveEvent();

  const isSaved = card.user_state === "saved";
  const isPending =
    dismissMut.isPending || saveMut.isPending || snoozeMut.isPending || unsaveMut.isPending;

  const handleAction = (e: React.MouseEvent, action: "dismiss" | "save" | "snooze" | "unsave") => {
    e.stopPropagation();
    if (action === "dismiss") dismissMut.mutate({ raceId: card.race_id });
    else if (action === "save") saveMut.mutate({ raceId: card.race_id });
    else if (action === "snooze") snoozeMut.mutate({ raceId: card.race_id, days: 30 });
    else unsaveMut.mutate({ raceId: card.race_id });
  };

  return (
    <Card
      variant="indigo"
      padding={12}
      role="button"
      tabIndex={0}
      onClick={() => nav(`/events/${card.race_id}`)}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") nav(`/events/${card.race_id}`);
      }}
      className="block w-full text-left hover:bg-bg-raised transition-colors space-y-2 cursor-pointer"
    >
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-1.5 text-[10px] mb-1 flex-wrap">
            <span className={`px-1.5 py-0.5 rounded-full ${tierBadge}`}>
              {tierLabel}
            </span>
            {card.is_lottery && (
              <span className="px-1.5 py-0.5 rounded-full bg-warn/15 text-warn ring-1 ring-warn/30">
                {t("events_lottery")}
              </span>
            )}
            {card.discipline !== "run" && (() => {
              // Phase 4 v149 — discipline chip deep-links к /sport-profile when
              // the registry maps the discipline; falls back к non-interactive
              // span for hyrox/duathlon (no sport-profile page yet).
              const sportSlug = disciplineToSportProfile(card.discipline);
              const label = card.discipline === "hyrox" ? "Hyrox" : t(`sport.${card.discipline}`);
              return sportSlug ? (
                <button
                  type="button"
                  data-testid={`events-card-discipline-link-${card.race_id}`}
                  onClick={(e) => {
                    e.stopPropagation();
                    nav(`/sport-profile/${sportSlug}`);
                  }}
                  className="px-1.5 py-0.5 rounded-full bg-fg-muted/10 text-fg-secondary ring-1 ring-fg-muted/20 capitalize hover:text-accent hover:ring-accent/40 transition-colors"
                >
                  {label}
                </button>
              ) : (
                <span className="px-1.5 py-0.5 rounded-full bg-fg-muted/10 text-fg-secondary ring-1 ring-fg-muted/20 capitalize">
                  {label}
                </span>
              );
            })()}
            {isSaved && (
              <span className="px-1.5 py-0.5 rounded-full bg-bad/15 text-bad ring-1 ring-bad/30 inline-flex items-center gap-1">
                <Heart className="w-2.5 h-2.5" /> {t("events_saved_badge")}
              </span>
            )}
            {card.coach_approved && (
              <span
                className="px-1.5 py-0.5 rounded-full bg-ok/15 text-ok ring-1 ring-ok/40 inline-flex items-center gap-1"
                title={t("events_coach_approved_title")}
              >
                <Check className="w-2.5 h-2.5" /> {t("events_coach_approved")}
              </span>
            )}
            <span className="text-fg-muted flex items-center gap-1">
              {disciplineIcon(card.discipline)}
              <span className="num-display">{card.distance_km.toFixed(1)} {kmShort}</span>
            </span>
          </div>
          <div className="font-semibold truncate">{nameForLang(card.name)}</div>
          <div className="text-xs text-fg-muted flex items-center gap-1.5 mt-0.5">
            <Calendar className="w-3 h-3" />
            <span>{fmtDate(card.next_race_date, t, i18n.language, card.typical_month)}</span>
          </div>
          {(card.city || card.country) && (
            <div className="text-xs text-fg-muted flex items-center gap-1.5">
              <MapPin className="w-3 h-3" />
              <span>
                {[card.city, card.country].filter(Boolean).join(", ")}
                {card.distance_to_athlete_km !== null && (
                  <span className="text-fg-dim ml-1">
                    · {fmtDistance(card.distance_to_athlete_km, kmShort)} {t("events_from_you")}
                  </span>
                )}
              </span>
            </div>
          )}
        </div>
        {card.achievability_pct !== null && card.achievability_label && (
          <div
            className={`shrink-0 text-center px-2 py-1.5 rounded-card ${tone.bg} ${tone.ring} min-w-[88px]`}
          >
            <div className={`text-[15px] font-bold num-display leading-tight ${tone.text}`}>
              {card.achievability_pct_low !== null && card.achievability_pct_high !== null
                ? `${card.achievability_pct_low}–${card.achievability_pct_high}%`
                : `${card.achievability_pct}%`}
            </div>
            <div className="text-[9px] text-fg-muted leading-tight max-w-[88px]">
              {card.achievability_label}
            </div>
          </div>
        )}
      </div>

      {/* W-REBUILD-6: длинное объяснение узкого места давилось в
          88px-колонку и превращалось в обрезанную кашу — полная строка. */}
      {card.bottleneck_explanation && (
        <div className="text-[11px] text-fg-muted leading-snug -mt-0.5">
          {t("events_bottleneck_prefix")}: {card.bottleneck_explanation}
        </div>
      )}

      {card.next_step_suggestion && (
        <div className="text-[11px] text-fg-muted flex items-center gap-1.5 -mt-0.5">
          <TrendUp className="w-3 h-3 text-accent/70" />
          <span>
            {t("events_next_step", {
              distance: card.next_step_suggestion.distance,
              weeks: card.next_step_suggestion.weeks,
            })}
          </span>
        </div>
      )}

      <div className="flex items-center justify-between gap-2">
        <ScoreFactors card={card} />
        {card.registration_url && (
          <span
            role="button"
            tabIndex={0}
            className="text-[10px] text-fg-muted flex items-center gap-1 cursor-pointer hover:text-fg-primary"
            onClick={(e) => {
              e.stopPropagation();
              window.open(card.registration_url!, "_blank", "noopener,noreferrer");
            }}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.stopPropagation();
                window.open(card.registration_url!, "_blank", "noopener,noreferrer");
              }
            }}
          >
            <ArrowSquareOut className="w-3 h-3" /> {t("events_site")}
          </span>
        )}
      </div>

      {showActions && (
        <div className="flex gap-1.5 pt-1 border-t border-bg-divider/50">
          {isSaved ? (
            <button
              type="button"
              disabled={isPending}
              onClick={(e) => handleAction(e, "unsave")}
              className="flex-1 px-2 py-1.5 rounded-pill bg-bad/15 text-bad ring-1 ring-bad/30 text-[11px] inline-flex items-center justify-center gap-1 disabled:opacity-50 hover:bg-bad/25 transition-colors"
            >
              <Heart className="w-3 h-3 fill-current" /> {t("events_unsave")}
            </button>
          ) : (
            <>
              <button
                type="button"
                disabled={isPending}
                onClick={(e) => handleAction(e, "dismiss")}
                title={t("events_hide_title")}
                className="flex-1 px-2 py-1.5 rounded-pill bg-bg-raised text-fg-muted ring-1 ring-bg-divider text-[11px] inline-flex items-center justify-center gap-1 disabled:opacity-50 hover:text-fg-primary hover:ring-bg-border transition-colors"
              >
                <X className="w-3 h-3" /> {t("events_hide")}
              </button>
              <button
                type="button"
                disabled={isPending}
                onClick={(e) => handleAction(e, "save")}
                title={t("events_save_title")}
                className="flex-1 px-2 py-1.5 rounded-pill bg-bad/10 text-bad ring-1 ring-bad/30 text-[11px] inline-flex items-center justify-center gap-1 disabled:opacity-50 hover:bg-bad/20 transition-colors"
              >
                <Heart className="w-3 h-3" /> {t("events_save_event")}
              </button>
              <button
                type="button"
                disabled={isPending}
                onClick={(e) => handleAction(e, "snooze")}
                title={t("events_snooze_title")}
                className="flex-1 px-2 py-1.5 rounded-pill bg-warn/10 text-warn ring-1 ring-warn/30 text-[11px] inline-flex items-center justify-center gap-1 disabled:opacity-50 hover:bg-warn/20 transition-colors"
              >
                <Clock className="w-3 h-3" /> {t("events_snooze")}
              </button>
            </>
          )}
        </div>
      )}
    </Card>
  );
}

// Added 10 and 25 km for city athletes who only want local races.
// "Везде" sentinel (999999) stays at the end so the slider index math is stable.
const RADIUS_STOPS = [10, 25, 50, 200, 500, 1000, 1500, 3000, 999999] as const;
const RADIUS_EVERYWHERE_IDX = RADIUS_STOPS.length - 1;

// D6 — map athlete.primary_sport (9 values) to race catalog discipline (6 values)
const SPORT_TO_RACE_DISCIPLINE: Record<string, RaceDiscipline | null> = {
  run:       "run",
  trail:     "trail",
  ride:      "ride",
  gravel:    "ride",
  swim:      "swim",
  triathlon: "triathlon",
  duathlon:  "duathlon",
  hyrox:     "hyrox",
  strength:  null,
};

export function EventsPage() {
  const { t } = useTranslation();
  const kmShort = t("km_short");
  const radiusLabel = (km: number) =>
    km === 999999 ? t("events_everywhere") : `${km} ${kmShort}`;
  const [adaptiveMatch, setAdaptiveMatch] = useState(true);
  const [discipline, setDiscipline] = useState<RaceDiscipline | "any">("any");

  // Bootstrap discipline filter from athlete's primary_sport
  useEffect(() => {
    api.get<{ primary_sport: string }>("/athlete/profile/sport-metrics")
      .then((r) => {
        const mapped = SPORT_TO_RACE_DISCIPLINE[r.data.primary_sport];
        if (mapped) setDiscipline(mapped);
      })
      .catch(() => {
        // silent: profile/sport-metrics fetch is optional preference hint;
        // discipline defaults to 'any' if it fails (acceptable UX).
      });
  }, []);
  const [onlyDated, setOnlyDated] = useState(true);
  // Single source of truth — slider/buckets auto-apply (debounced via React
  // query). No pending-vs-applied split: the old "Apply" button confused
  // first-time users who didn't realise the filter hadn't yet taken effect.
  const [radiusIdx, setRadiusIdx] = useState<number>(RADIUS_EVERYWHERE_IDX);
  const [horizonDays, setHorizonDays] = useState<number>(365);
  const [search, setSearch] = useState("");
  // Sprint 39g — distance preset key, null = «все дистанции».
  const [distancePreset, setDistancePreset] = useState<string | null>(null);
  const [sortBy, setSortBy] = useState<"score" | "date" | "achievability">("score");
  // Sess 07 — UX evolution
  const [groupMode, setGroupMode] = useState<"date" | "readiness">("date");
  const [filterByActiveGoal, setFilterByActiveGoal] = useState(false);
  const [showSortHelp, setShowSortHelp] = useState(false);
  // v146 — Calendar view (12-week timeline). Persisted в localStorage
  // чтобы athlete не переключал каждый раз.
  const [viewMode, setViewMode] = useState<"list" | "calendar">(() => {
    if (typeof window === "undefined") return "list";
    const saved = window.localStorage.getItem("events.viewMode");
    return saved === "calendar" ? "calendar" : "list";
  });
  const setViewModePersisted = (mode: "list" | "calendar") => {
    setViewMode(mode);
    if (typeof window !== "undefined") {
      window.localStorage.setItem("events.viewMode", mode);
    }
  };

  const presetsForDiscipline = useMemo(() => {
    if (discipline === "any") return [];
    return DISTANCE_PRESETS[discipline] || [];
  }, [discipline]);

  const activePreset = useMemo(() => {
    if (!distancePreset) return null;
    return presetsForDiscipline.find((p) => p.key === distancePreset) || null;
  }, [distancePreset, presetsForDiscipline]);

  const radiusKm = RADIUS_STOPS[radiusIdx];
  // Radius применяется ДАЖЕ при адаптивном подборе — это user override.
  const sendRadius = radiusKm !== 999999;

  const { data, isLoading, isError } = useEventRecommendations({
    discipline: discipline === "any" || adaptiveMatch ? undefined : discipline,
    only_with_date: adaptiveMatch ? false : onlyDated,
    smart_match: adaptiveMatch,
    horizon_days: horizonDays,
    limit: 30,
    ...(sendRadius ? { radius_km: radiusKm } : {}),
    // Sprint 39g — distance preset (только manual mode, адаптивный подбор решает сам).
    ...(activePreset && !adaptiveMatch ? {
      distance_min_km: activePreset.minKm,
      distance_max_km: activePreset.maxKm,
    } : {}),
  });

  const allItems = data?.items ?? [];
  // клиентский text-фильтр + сортировка
  const items = useMemo(() => {
    const q = search.trim().toLowerCase();
    let filtered = q
      ? allItems.filter((it) => {
          const haystack = [it.name, it.city || "", it.country || ""].join(" ").toLowerCase();
          return haystack.includes(q);
        })
      : allItems;
    // Sess 07 — chip "по моей цели": проксируем через factor_goal (1-5),
    // ≥ 4 = strong match с active goal в backend scoring.
    if (filterByActiveGoal && data?.athlete_has_active_goal) {
      filtered = filtered.filter((it) => (it.factor_goal ?? 0) >= 4);
    }
    if (sortBy === "date") {
      filtered = [...filtered].sort((a, b) => {
        const da = a.next_race_date ?? (a.typical_month ? `${new Date().getFullYear() + (a.typical_month < new Date().getMonth() + 1 ? 1 : 0)}-${String(a.typical_month).padStart(2, "0")}-15` : "9999");
        const db = b.next_race_date ?? (b.typical_month ? `${new Date().getFullYear() + (b.typical_month < new Date().getMonth() + 1 ? 1 : 0)}-${String(b.typical_month).padStart(2, "0")}-15` : "9999");
        return da < db ? -1 : 1;
      });
    } else if (sortBy === "achievability") {
      filtered = [...filtered].sort((a, b) => (b.achievability_pct ?? 0) - (a.achievability_pct ?? 0));
    }
    return filtered;
  }, [allItems, search, sortBy, filterByActiveGoal, data?.athlete_has_active_goal]);

  const grouped = useMemo(() => {
    // Sess 07 — два режима группировки. По датам (исторический) или
    // по готовности (новый).
    if (groupMode === "readiness") {
      const groups: { key: string; label: string; items: EventCard[] }[] = [
        { key: "ready",       label: t("events_group_ready"),       items: [] },
        { key: "training",    label: t("events_group_training"),    items: [] },
        { key: "ambitious",   label: t("events_group_ambitious"),   items: [] },
        { key: "unrealistic", label: t("events_group_unrealistic"), items: [] },
        { key: "tba",         label: t("events_group_tba"),         items: [] },
      ];
      for (const it of items) {
        if (!it.next_race_date) { groups[4].items.push(it); continue; }
        const pct = it.achievability_pct;
        if (pct === null) { groups[2].items.push(it); continue; }
        if (pct >= 70) groups[0].items.push(it);
        else if (pct >= 40) groups[1].items.push(it);
        else if (pct >= 20) groups[2].items.push(it);
        else groups[3].items.push(it);
      }
      return groups.filter((g) => g.items.length > 0);
    }
    const groups: { key: string; label: string; items: EventCard[] }[] = [
      { key: "near",    label: t("events_group_near"),    items: [] },
      { key: "mid",     label: t("events_group_mid"),     items: [] },
      { key: "far",     label: t("events_group_far"),     items: [] },
      { key: "no_date", label: t("events_group_no_date"), items: [] },
    ];
    const now = Date.now();
    for (const it of items) {
      if (!it.next_race_date) {
        groups[3].items.push(it);
        continue;
      }
      const days = Math.round((new Date(it.next_race_date).getTime() - now) / 86400000);
      if (days <= 90) groups[0].items.push(it);
      else if (days <= 180) groups[1].items.push(it);
      else groups[2].items.push(it);
    }
    return groups.filter((g) => g.items.length > 0);
  }, [items, groupMode, t]);

  const savedCount = data?.saved_count ?? 0;
  const nav = useNavigate();

  return (
    <div
      data-testid="events-page"
      className="px-4 py-3 max-w-7xl mx-auto lg:px-6"
    >
      <div className="flex items-end gap-2 justify-between mb-3">
        <div>
          <div className="text-[9.5px] uppercase tracking-[0.22em] font-bold text-fg-muted">
            {t("events_eyebrow", { defaultValue: "STORICA · СТАРТЫ" })}
          </div>
          <h1 className="text-[22px] font-bold text-fg-primary tracking-tight" style={{ letterSpacing: "-0.02em" }}>{t("events_title")}</h1>
        </div>
        {savedCount > 0 && (
          <button
            type="button"
            onClick={() => nav("/events/saved")}
            className="px-2.5 py-1 rounded-pill bg-bad/10 text-bad ring-1 ring-bad/30 text-[11px] inline-flex items-center gap-1 hover:bg-bad/20 transition-colors"
          >
            <Heart className="w-3 h-3 fill-current" />
            {t("events_saved_count", { count: savedCount })}
          </button>
        )}
      </div>

      {/* D-11 (Cycle 8) — two-column layout on lg+:
            • Left rail (sticky): search + adaptive-match toggle + manual
              filters. Athlete tweaks filters without scrolling away
              from the results.
            • Right column: results list (group toggle + sort + cards).
          Mobile keeps the original stacked flow — no rail, filters
          stay above results so the small viewport doesn't fight the
          thumb. */}
      <div className="lg:grid lg:grid-cols-[260px_1fr] lg:gap-5 lg:items-start space-y-3 lg:space-y-0">
      <aside
        data-testid="events-filter-sidebar"
        className="space-y-3 lg:sticky lg:top-4 lg:self-start lg:max-h-[calc(100vh-2rem)] lg:overflow-y-auto lg:pr-1"
      >
      {/* Sprint 39f — text search */}
      <Input
        bare
        type="search"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        placeholder={t("events_search_placeholder")}
        className="w-full lg:max-w-md px-3 py-2 rounded-pill bg-bg-raised border border-bg-border text-sm placeholder:text-fg-muted focus:outline-none focus:border-accent"
      />

      {/* Adaptive match toggle */}
      <button
        type="button"
        onClick={() => setAdaptiveMatch((v) => !v)}
        className={`w-full px-3 py-2 rounded-card border flex items-center justify-between gap-2 text-left transition-colors ${
          adaptiveMatch
            ? "bg-accent/10 text-fg-primary border-accent/60"
            : "bg-bg-raised text-fg-muted border-bg-border hover:border-bg-divider"
        }`}
      >
        <div className="flex items-center gap-2 min-w-0">
          <Sparkle
            className={`w-4 h-4 ${adaptiveMatch ? "text-accent" : "text-fg-muted"}`}
          />
          <div className="min-w-0">
            <div className="text-sm font-medium">
              {t("events_adaptive_label")} · {adaptiveMatch ? t("events_adaptive_on", { defaultValue: "вкл" }) : t("events_adaptive_off", { defaultValue: "выкл" })}
            </div>
            <div className="text-[10px] text-fg-muted truncate">
              {adaptiveMatch ? t("events_smart_on") : t("events_smart_off")}
            </div>
          </div>
        </div>
        <div
          className={`w-9 h-5 rounded-full relative transition-colors shrink-0 ${
            adaptiveMatch ? "bg-accent" : "bg-bg-divider"
          }`}
        >
          <span
            className={`absolute top-0.5 w-4 h-4 rounded-full bg-white transition-transform ${
              adaptiveMatch ? "translate-x-4" : "translate-x-0.5"
            }`}
          />
        </div>
      </button>

      {adaptiveMatch && (data?.applied_filters?.length ?? 0) > 0 && (
        <div className="flex gap-1.5 flex-wrap text-[10px]">
          {data!.applied_filters.map((c) => (
            <span
              key={c.key}
              className="px-1.5 py-0.5 rounded-full bg-accent/10 text-accent ring-1 ring-accent/30"
            >
              {c.label}
            </span>
          ))}
        </div>
      )}

      {/* Manual Filters — спрятаны при адаптивном подборе ON */}
      {!adaptiveMatch && (
        <div className="space-y-3">
          <div className="flex gap-1.5 overflow-x-auto -mx-1 px-1 scrollbar-hide">
            {DISCIPLINE_OPTIONS.map((o) => {
              const on = discipline === o.key;
              return (
                <button
                  key={o.key}
                  type="button"
                  onClick={() => {
                    setDiscipline(o.key);
                    setDistancePreset(null);  // reset preset when discipline changes
                  }}
                  className={`shrink-0 px-2.5 py-1 rounded-pill border text-[11px] transition-colors ${
                    on
                      ? "bg-accent/15 text-accent border-accent/60"
                      : "bg-bg-raised text-fg-muted border-bg-border hover:text-fg-primary"
                  }`}
                >
                  {t(o.i18nKey)}
                </button>
              );
            })}
          </div>

          {/* Sprint 39g — distance preset chips для выбранной дисциплины */}
          {presetsForDiscipline.length > 0 && (
            <div className="flex gap-1.5 overflow-x-auto -mx-1 px-1 scrollbar-hide">
              <button
                type="button"
                onClick={() => setDistancePreset(null)}
                className={`shrink-0 px-2.5 py-1 rounded-pill border text-[11px] transition-colors ${
                  distancePreset === null
                    ? "bg-accent/15 text-accent border-accent/60"
                    : "bg-bg-raised text-fg-muted border-bg-border hover:text-fg-primary"
                }`}
              >
                {t("events_all_distances")}
              </button>
              {presetsForDiscipline.map((p) => {
                const on = distancePreset === p.key;
                return (
                  <button
                    key={p.key}
                    type="button"
                    onClick={() => setDistancePreset(p.key)}
                    className={`shrink-0 px-2.5 py-1 rounded-pill border text-[11px] transition-colors ${
                      on
                        ? "bg-accent/15 text-accent border-accent/60"
                        : "bg-bg-raised text-fg-muted border-bg-border hover:text-fg-primary"
                    }`}
                  >
                    {t(p.i18nKey)}
                  </button>
                );
              })}
            </div>
          )}

          {/* Radius slider + clickable bucket chips. Auto-applies on change. */}
          {data?.athlete_has_coords ? (
            <div className="space-y-1.5">
              <div className="flex items-baseline justify-between">
                <span className="text-[11px] text-fg-muted">{t("events_radius")}</span>
                <span className="text-[11px] text-accent num-display">
                  {radiusLabel(RADIUS_STOPS[radiusIdx])}
                </span>
              </div>
              <input
                type="range"
                min={0}
                max={RADIUS_STOPS.length - 1}
                step={1}
                value={radiusIdx}
                onChange={(e) => setRadiusIdx(Number(e.target.value))}
                className="w-full accent-accent"
              />
              {/* Quick-pick chips: clicking jumps the slider directly. Bucket
                  counts come from the API for current location. We add the
                  small (10/25 km) and "Везде" chips even if the API didn't
                  return buckets for them, so city users get a fast path. */}
              <div className="flex flex-wrap gap-1 text-[10px]">
                {RADIUS_STOPS.map((km, idx) => {
                  const apiBucket = (data?.radius_buckets ?? []).find((b) => b.km === km);
                  const count = apiBucket?.count;
                  const active = idx === radiusIdx;
                  return (
                    <button
                      key={km}
                      type="button"
                      onClick={() => setRadiusIdx(idx)}
                      className={`px-2 py-1 rounded-full ring-1 shrink-0 transition-colors ${
                        active
                          ? "bg-accent/20 text-accent ring-accent/50"
                          : count !== undefined && count > 0
                          ? "bg-bg-raised text-fg-secondary ring-bg-divider hover:bg-bg-divider/40"
                          : "bg-bg-raised/40 text-fg-dim ring-bg-border/40 hover:bg-bg-raised/60"
                      }`}
                    >
                      {radiusLabel(km)}
                      {count !== undefined && <span className="ml-1 opacity-70">· {count}</span>}
                    </button>
                  );
                })}
              </div>
            </div>
          ) : (
            <GeolocationPrompt />
          )}

          <label className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-pill border border-bg-border bg-bg-raised text-[11px] cursor-pointer">
            <input
              type="checkbox"
              checked={onlyDated}
              onChange={(e) => setOnlyDated(e.target.checked)}
              className="accent-accent"
            />
            <span className="text-fg-muted">{t("events_with_date")}</span>
          </label>

          {/* Sprint 37 F14: timeline horizon chips */}
          <div className="flex items-center gap-1 flex-wrap">
            <span className="text-[10px] text-fg-muted mr-1">{t("events_horizon")}:</span>
            {[
              { days: 90,  label: t("events_horizon_3m") },
              { days: 180, label: t("events_horizon_6m") },
              { days: 365, label: t("events_horizon_1y") },
              { days: 730, label: t("events_horizon_2y") },
            ].map((h) => (
              <button
                key={h.days}
                type="button"
                onClick={() => setHorizonDays(h.days)}
                className={`px-2 py-0.5 rounded-pill text-[11px] ring-1 transition-colors ${
                  horizonDays === h.days
                    ? "bg-accent/15 text-accent ring-accent/40"
                    : "bg-bg-raised text-fg-secondary ring-bg-divider hover:text-fg-primary"
                }`}
              >
                {h.label}
              </button>
            ))}
          </div>
        </div>
      )}
      </aside>

      <main className="space-y-3 min-w-0">
      {isLoading && <div className="text-fg-muted text-sm">{t("events_loading")}</div>}
      {isError && <div className="text-bad text-sm">{t("events_error")}</div>}

      {!isLoading && items.length === 0 && (
        <EmptyState
          icon={CalendarBlank}
          title={t("events_empty_title")}
          description={adaptiveMatch ? t("events_empty_smart") : t("events_empty_manual")}
          testId="events-empty"
          action={adaptiveMatch ? (
            <button
              type="button"
              onClick={() => setAdaptiveMatch(false)}
              className="px-4 py-1.5 rounded-pill ring-1 ring-accent/40 bg-accent/10 text-accent text-[12px] font-medium"
            >
              {t("events_disable_smart")}
            </button>
          ) : undefined}
        />
      )}

      {/* v146 — View mode toggle (list vs calendar). Hidden mobile <md
          because 12-week timeline doesn't fit narrow viewport. */}
      {items.length > 0 && (
        <div className="hidden md:flex items-center gap-1.5 text-[11px] flex-wrap">
          <span className="text-fg-muted shrink-0">
            {t("events_view_label", { defaultValue: "Вид:" })}
          </span>
          {(
            [
              { key: "list", label: t("events_view_list", { defaultValue: "Список" }) },
              { key: "calendar", label: t("events_view_calendar", { defaultValue: "12 недель" }) },
            ] as { key: "list" | "calendar"; label: string }[]
          ).map((opt) => (
            <button
              key={opt.key}
              type="button"
              onClick={() => setViewModePersisted(opt.key)}
              data-testid={`events-view-${opt.key}`}
              className={`px-2 py-0.5 rounded-pill ring-1 transition-colors ${
                viewMode === opt.key
                  ? "bg-accent/15 text-accent ring-accent/40"
                  : "bg-bg-raised text-fg-secondary ring-bg-divider hover:text-fg-primary"
              }`}
            >
              {opt.label}
            </button>
          ))}
        </div>
      )}

      {/* Sess 07 — group mode toggle + my-goal chip */}
      {items.length > 0 && viewMode === "list" && (
        <div className="space-y-1.5">
          <div className="flex items-center gap-1.5 text-[11px] flex-wrap">
            <span className="text-fg-muted shrink-0">{t("events_group_label")}</span>
            {(
              [
                { key: "date",      label: t("events_group_mode_date") },
                { key: "readiness", label: t("events_group_mode_readiness") },
              ] as { key: "date" | "readiness"; label: string }[]
            ).map((opt) => (
              <button
                key={opt.key}
                type="button"
                onClick={() => setGroupMode(opt.key)}
                className={`px-2 py-0.5 rounded-pill ring-1 transition-colors ${
                  groupMode === opt.key
                    ? "bg-accent/15 text-accent ring-accent/40"
                    : "bg-bg-raised text-fg-secondary ring-bg-divider hover:text-fg-primary"
                }`}
              >
                {opt.label}
              </button>
            ))}
            {data?.athlete_has_active_goal && (
              <button
                type="button"
                onClick={() => setFilterByActiveGoal((v) => !v)}
                className={`px-2 py-0.5 rounded-pill ring-1 transition-colors ml-auto ${
                  filterByActiveGoal
                    ? "bg-accent/15 text-accent ring-accent/40"
                    : "bg-bg-raised text-fg-secondary ring-bg-divider hover:text-fg-primary"
                }`}
              >
                {t("events_filter_my_goal")}
              </button>
            )}
          </div>
        </div>
      )}

      {/* Сортировка — only в list mode (calendar sort'ит по дате) */}
      {items.length > 0 && viewMode === "list" && (
        <div className="flex items-center gap-1.5 text-[11px] flex-wrap">
          <span className="text-fg-muted shrink-0">{t("events_sort_label")}</span>
          {(
            [
              { key: "score",         label: t("events_sort_score") },
              { key: "date",          label: t("events_sort_date") },
              { key: "achievability", label: t("events_sort_achievability") },
            ] as { key: "score" | "date" | "achievability"; label: string }[]
          ).map((opt) => (
            <button
              key={opt.key}
              type="button"
              onClick={() => setSortBy(opt.key)}
              className={`px-2 py-0.5 rounded-pill ring-1 transition-colors ${
                sortBy === opt.key
                  ? "bg-accent/15 text-accent ring-accent/40"
                  : "bg-bg-raised text-fg-secondary ring-bg-divider hover:text-fg-primary"
              }`}
            >
              {opt.label}
            </button>
          ))}
          <button
            type="button"
            onClick={() => setShowSortHelp((v) => !v)}
            className="ml-1 px-1.5 py-0.5 rounded-[3px] text-fg-muted hover:text-fg-primary text-[10px] ring-1 ring-bg-divider"
            aria-label={t("events_sort_help_label")}
          >
            ?
          </button>
        </div>
      )}
      {showSortHelp && (
        <Card variant="indigo" padding={8} className="text-[11px] text-fg-muted">
          {sortBy === "score"
            ? t("events_sort_score_help")
            : sortBy === "achievability"
              ? t("events_sort_achievability_help")
              : t("events_sort_date_help")}
        </Card>
      )}

      {viewMode === "calendar" && items.length > 0 && (
        <CalendarTimeline items={items} t={t} />
      )}

      {viewMode === "list" && grouped.map((g) => (
        <section key={g.key} className="space-y-2">
          <div className="label-caps">{g.label}</div>
          {(g.key === "tba" || g.key === "no_date") && (
            <div className="text-[11px] text-fg-muted">{t("events_tba_help")}</div>
          )}
          {/* Desktop: 2-col event grid (3-col on very wide screens).
              Mobile preserves the single-column stack — cards already
              have everything an athlete needs on a narrow viewport. */}
          <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-2">
            {g.items.map((it) => (
              <EventCardItem key={it.race_id} card={it} />
            ))}
          </div>
        </section>
      ))}

      {/* Sprint 39e — submit missing event */}
      <SuggestMissingEventCTA />
      </main>
      </div>
      <FABSpeedDial
        icon={Plus}
        label={t("fab.events.label")}
        actions={[
          {
            icon: Trophy,
            label: t("fab.events.primary"),
            hint: t("fab.events.primary_hint"),
            onClick: () => nav("/quick-log?source=race"),
            testId: "fab-events-primary",
          },
          {
            icon: MagnifyingGlass,
            label: t("fab.events.search"),
            hint: t("fab.events.search_hint"),
            onClick: () => window.scrollTo({ top: 0, behavior: "smooth" }),
            testId: "fab-events-search",
          },
        ]}
        testId="fab-events"
      />
    </div>
  );
}

interface CalendarTimelineProps {
  items: EventCard[];
  t: (key: string, opts?: Record<string, unknown>) => string;
}

/**
 * CalendarTimeline — 12-week horizontal timeline view of upcoming events.
 *
 * Mockup canon: «Suggested for you» section в P3::ScreenEvents shows
 * events distributed across a 12-week scroll. List view shows them
 * grouped by readiness/date; calendar view shows them positioned in time.
 *
 * Architecture:
 *   • 12 week buckets starting from the upcoming Monday
 *   • Each bucket = vertical column with events that fall in that week
 *   • Today marker (orange dot) shows current week position
 *   • Events outside 12-week window get a separate "Beyond" footer
 *   • Mobile (<md) is hidden via parent — timeline doesn't fit narrow viewport
 */
function CalendarTimeline({ items, t }: CalendarTimelineProps) {
  const buckets = useMemo(() => buildWeekBuckets(items), [items]);
  const beyond = useMemo(
    () =>
      items.filter((it) => {
        if (!it.next_race_date) return false;
        const days = Math.round(
          (new Date(it.next_race_date).getTime() - Date.now()) / 86400000,
        );
        return days > 7 * 12;
      }),
    [items],
  );
  const noDate = useMemo(
    () => items.filter((it) => !it.next_race_date),
    [items],
  );

  return (
    <section
      data-testid="events-calendar-timeline"
      className="space-y-3"
    >
      <div className="text-[10px] font-mono font-bold uppercase tracking-[0.22em] text-accent">
        {t("events_calendar_eyebrow", {
          defaultValue: "БЛИЖАЙШИЕ 12 НЕДЕЛЬ",
        })}
      </div>

      <div className="overflow-x-auto pb-2">
        <div className="grid gap-2" style={{ gridTemplateColumns: "repeat(12, 220px)" }}>
          {buckets.map((bucket, idx) => (
            <div
              key={idx}
              data-testid={`events-calendar-bucket-${idx}`}
              className={`flex flex-col gap-2 rounded-card p-2.5 ${
                bucket.isCurrentWeek
                  ? "bg-accent/[0.08] ring-1 ring-accent/30"
                  : "bg-bg-card"
              }`}
            >
              <div className="flex items-baseline justify-between">
                <span className="text-[10px] font-mono font-bold uppercase tracking-[0.16em] text-fg-secondary">
                  {bucket.isCurrentWeek
                    ? t("events_calendar_this_week", { defaultValue: "ЭТА НЕДЕЛЯ" })
                    : t("events_calendar_week_short", {
                        defaultValue: "Нед {{n}}",
                        n: idx + 1,
                      })}
                </span>
                <span className="text-[9.5px] font-mono tabular-nums text-fg-muted">
                  {bucket.startLabel}
                </span>
              </div>
              {bucket.items.length === 0 ? (
                <span className="text-[10px] text-fg-dim italic">
                  {t("events_calendar_empty_week", { defaultValue: "—" })}
                </span>
              ) : (
                <div className="flex flex-col gap-1.5">
                  {bucket.items.slice(0, 4).map((it) => (
                    <CalendarEventChip key={it.race_id} card={it} t={t} />
                  ))}
                  {bucket.items.length > 4 && (
                    <span className="text-[9.5px] text-fg-muted">
                      +{bucket.items.length - 4}
                    </span>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      {beyond.length > 0 && (
        <Card
          variant="indigo"
          padding={12}
          data-testid="events-calendar-beyond"
          className="space-y-1.5"
        >
          <div className="text-[10px] font-mono font-bold uppercase tracking-[0.22em] text-fg-muted">
            {t("events_calendar_beyond_eyebrow", {
              defaultValue: "ПОЗЖЕ · {{n}}",
              n: beyond.length,
            })}
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-2">
            {beyond.slice(0, 6).map((it) => (
              <EventCardItem key={it.race_id} card={it} />
            ))}
          </div>
          {beyond.length > 6 && (
            <div className="text-[10px] text-fg-muted">
              +{beyond.length - 6}{" "}
              {t("events_calendar_beyond_more", { defaultValue: "событий" })}
            </div>
          )}
        </Card>
      )}

      {noDate.length > 0 && (
        <Card
          variant="indigo"
          padding={12}
          data-testid="events-calendar-tba"
          className="space-y-1.5"
        >
          <div className="text-[10px] font-mono font-bold uppercase tracking-[0.22em] text-fg-muted">
            {t("events_group_tba", { defaultValue: "ДАТА НЕ ОБЪЯВЛЕНА" })} ·{" "}
            {noDate.length}
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-2">
            {noDate.slice(0, 6).map((it) => (
              <EventCardItem key={it.race_id} card={it} />
            ))}
          </div>
        </Card>
      )}
    </section>
  );
}

export interface WeekBucket {
  startISO: string;
  startLabel: string;
  isCurrentWeek: boolean;
  items: EventCard[];
}

export function buildWeekBuckets(items: EventCard[]): WeekBucket[] {
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  // Monday of current week (Mon = 1, Sun = 0)
  const dayOfWeek = today.getDay();
  const daysFromMonday = dayOfWeek === 0 ? 6 : dayOfWeek - 1;
  const currentMonday = new Date(today);
  currentMonday.setDate(today.getDate() - daysFromMonday);

  const buckets: WeekBucket[] = [];
  for (let i = 0; i < 12; i++) {
    const start = new Date(currentMonday);
    start.setDate(currentMonday.getDate() + i * 7);
    const end = new Date(start);
    end.setDate(start.getDate() + 6);
    const startISO = isoDate(start);
    const endISO = isoDate(end);
    const inBucket = items.filter((it) => {
      if (!it.next_race_date) return false;
      return it.next_race_date >= startISO && it.next_race_date <= endISO;
    });
    buckets.push({
      startISO,
      startLabel: `${String(start.getDate()).padStart(2, "0")}.${String(start.getMonth() + 1).padStart(2, "0")}`,
      isCurrentWeek: i === 0,
      items: inBucket,
    });
  }
  return buckets;
}

function isoDate(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

function CalendarEventChip({
  card,
  t,
}: {
  card: EventCard;
  t: (key: string, opts?: Record<string, unknown>) => string;
}) {
  const nav = useNavigate();
  // Tier identity tokens — match TIER_COLOR registry (wmm = amber/gold,
  // major_local = violet plum). Calendar chip earlier diverged (emerald) —
  // restored to canon palette parity here.
  const tierColor =
    card.tier === "wmm"
      ? "bg-warn/15 text-warn ring-warn/40"
      : card.tier === "major_local"
        ? "bg-accent/15 text-accent ring-accent/40"
        : "bg-bg-raised text-fg-secondary ring-bg-divider";
  // EventCard.name is Record<locale, string> — pick best match.
  const displayName =
    card.name?.ru ||
    card.name?.en ||
    Object.values(card.name ?? {})[0] ||
    t("events_calendar_unnamed", { defaultValue: "Гонка" });
  return (
    <button
      type="button"
      onClick={() => nav(`/events/${card.race_id}`)}
      data-testid={`events-calendar-chip-${card.race_id}`}
      className="text-left rounded-md bg-bg-raised hover:bg-bg-surface px-2 py-1.5 transition-colors min-w-0"
    >
      <div className="text-[11px] font-semibold text-fg-primary truncate">
        {displayName}
      </div>
      <div className="flex items-center gap-1.5 mt-0.5">
        {card.tier && (
          <span
            className={`text-[8.5px] font-mono uppercase tracking-[0.10em] px-1 py-0.5 rounded ring-1 ${tierColor}`}
          >
            {card.tier}
          </span>
        )}
        {card.achievability_pct != null && (
          <span
            className={`text-[9px] font-mono tabular-nums ${
              card.achievability_pct >= 70
                ? "text-ok"
                : card.achievability_pct >= 40
                  ? "text-warn"
                  : "text-bad"
            }`}
          >
            {card.achievability_pct}%
          </span>
        )}
      </div>
    </button>
  );
}
